import type { FooterCopy } from '@/lib/app-shell/copy'
import { DEFAULT_DISPLAY_TIMEZONE, todayInTimeZone } from '@/i18n/dates'
import type { Locale } from '@/i18n/locales'

/**
 * The site footer's navigation, built per locale for the global shell (`FooterContainer`).
 *
 * Same split as the navbar's `navigation/menu-config.ts`:
 *
 *   - **Copy** — section titles, link names, the description and copyright line — lives
 *     in the translation store (`src/content/translations/app-shell/footer.json`) and arrives
 *     rendered, so translators own every visible string.
 *   - **Structure** — which routes the footer promotes and the paths they point at — is
 *     editorial routing data, not translatable prose, so it stays here. It does not
 *     vary by locale: the footer lists the site's evergreen surfaces, which every
 *     locale serves.
 */

/**
 * A footer destination, discriminated by which kind it is rather than by a flag:
 *
 * - `route` — a **nexus route**. Rendered through the locale-aware `Link`, which
 *   supplies the prefix — or correctly supplies none, for the locale-free
 *   `/blog/` surfaces and for an already-prefixed route pinned at its serving
 *   locale (`invariants.md#single-locale-pages-pinned`). Usually
 *   locale-agnostic, therefore, but not always.
 * - `href` — anything nexus does not serve: the legacy `/en-us/**` tree, and
 *   off-site URLs. Rendered through `Link`'s native form — a plain anchor, because a
 *   routed link would prefetch and client-navigate a page the app cannot render — which
 *   is also what carries the selected display currency onto it.
 */
export type FooterLink = { name: string } & ({ route: string } | { href: string })

export interface FooterSection {
    title: string
    links: FooterLink[]
}

/**
 * Prefix a **legacy** site path with the active locale: `/carrier/` → `/es-mx/carrier/`.
 *
 * Only for paths nexus does not serve — for someone else's tree, where no helper will
 * supply the prefix. A nexus route uses `route:` instead and lets `Link` resolve it, so
 * this does not duplicate that decision. (A route pinned at its serving locale also
 * carries a hand-written prefix, but it is a literal in the structure below rather than
 * a per-locale computation, so it does not come through here.)
 */
function localePath(locale: Locale, path: string): string {
    return `/${locale}${path}`
}

/**
 * The blog and the help centre are shared surfaces served outside the locale tree, so
 * they are absolute and carry no locale prefix.
 */
const BLOG_PATH = '/blog/'
const PRESS_PATH = '/blog/press/'
const HELP_URL = 'https://help.wanderu.com/'

export function buildFooterSections(locale: Locale, copy: FooterCopy): FooterSection[] {
    return [
        {
            title: copy.travel.text,
            links: [
                { name: copy.busTickets.text, href: localePath(locale, '/bus-tickets/') },
                { name: copy.trainTickets.text, href: localePath(locale, '/train-tickets/') },
            ],
        },
        {
            title: copy.about.text,
            links: [
                // Legacy: nexus serves `/about-us/careers/` but has no page at
                // `/about-us/` itself — a `route` here would prefetch a 404 (#892).
                { name: copy.aboutUs.text, href: localePath(locale, '/about-us/') },
                { name: copy.blog.text, route: BLOG_PATH },
                { name: copy.press.text, route: PRESS_PATH },
                // Both pinned: the careers and partner surfaces are default-locale-only
                // by design, so the reader's locale would only earn a redirect
                // (`invariants.md#single-locale-pages-pinned`).
                { name: copy.careers.text, route: '/en-us/about-us/careers/' },
                { name: copy.partnerWithUs.text, route: '/en-us/partners/' },
            ],
        },
        {
            title: copy.customerService.text,
            links: [
                { name: copy.contactUs.text, href: HELP_URL },
                { name: copy.companies.text, href: localePath(locale, '/carrier/') },
            ],
        },
    ]
}

/**
 * The legal links, whose **names** localize but whose **targets** do not.
 *
 * Both documents exist in one authoritative language, so each is pinned at its `/en-us/`
 * URL for every locale (`invariants.md#single-locale-pages-pinned`) — hence no `locale`
 * parameter. A locale-agnostic `route` here would be prefixed with the reader's locale
 * into a URL `src/proxy.ts` immediately coerces back, costing the reader a redirect on a
 * page that is never going to gain their locale.
 *
 * They stay `route` rather than `href` because nexus serves them: an already-prefixed
 * href reaches `Link` and renders verbatim (`needsNoLocalePrefix`), so pinning the target
 * keeps client-side navigation instead of trading it away for a plain anchor.
 */
export function buildFooterLegalLinks(copy: FooterCopy): FooterLink[] {
    return [
        { name: copy.termsAndConditions.text, route: '/en-us/terms-and-conditions/' },
        { name: copy.privacyPolicy.text, route: '/en-us/privacy-policy/' },
    ]
}

/**
 * The copyright year, projected in the app's display timezone rather than read off the
 * runtime's own zone — the footer renders on both sides of hydration, and an unpinned
 * `getFullYear()` disagrees between the server (UTC) and an Eastern client for the last
 * five hours of December 31 (`invariants.md#ssr-safe-rendered-dates`).
 */
export function copyrightYear(): string {
    return todayInTimeZone(DEFAULT_DISPLAY_TIMEZONE).slice(0, 4)
}
